import type { FactionId } from '../../shared/types';

/**
 * IDEA-014 「門派 BGM 變奏」 — 5 門派各一種 pitch + filter 變奏 preset.
 *
 * Re-uses the existing battle BGM (synth notes OR downloaded mp3) and tilts
 * its character via two cheap Web Audio knobs:
 *   - `playbackRate` — equal-temperament semitone shifts in [0.85, 1.15]
 *     (~±2 semitones max). Applies to file mode (<audio>.playbackRate) and
 *     synth mode (note frequency multiplier inside MusicPlayer).
 *   - Biquad filter (synth mode only) — low-pass or high-pass cutoff that
 *     warms or brightens the timbre without changing volume.
 *
 * Player gets one preset based on their strongest faction (see
 * `pickDisplayFaction` in `src/game/entities/sprite-keys.ts`). Unknown / null
 * faction → NEUTRAL_PRESET (no shift, no filter).
 *
 * Volume is intentionally untouched — no surprise loud/quiet jumps mid-battle.
 *
 * Intent map:
 *   ⚡ celestial (星算/數學, purple)  → +1 semitone + low-pass  — bright but warm
 *   🔮 runic     (符文/國語, magenta) → -1 semitone + low-pass  — grounded, mystic
 *   🤖 techno    (機械/英語, blue)    → +2 semitones + high-pass — clean, digital
 *   🌀 elemental (自然/自然, teal)    → unchanged + low-pass     — soft, organic
 *   🏰 dominion  (疆域/社會, brown)   → -2 semitones + low-pass  — heavy, anchored
 */

export type FilterShape = 'lowpass' | 'highpass' | 'none';

export interface FactionBgmPreset {
  /** Playback rate multiplier. 1.0 = no change. Bounded to [0.85, 1.15]. */
  playbackRate: number;
  /** Filter shape applied to synth-mode output. 'none' = pass-through. */
  filterType: FilterShape;
  /** Biquad cutoff frequency in Hz (ignored when filterType === 'none'). */
  filterCutoffHz: number;
  /** Biquad Q (resonance). Mild values 0.5..1.5 to avoid honkiness. */
  filterQ: number;
}

/** Hard bounds enforced regardless of preset source. */
export const PLAYBACK_RATE_MIN = 0.85;
export const PLAYBACK_RATE_MAX = 1.15;

/** Default / fallback preset. No pitch shift, no filter tilt. */
export const NEUTRAL_PRESET: FactionBgmPreset = {
  playbackRate: 1.0,
  filterType: 'none',
  filterCutoffHz: 20_000,
  filterQ: 0.707,
};

// Equal-temperament semitone ratios.
const SEMITONE_UP_1 = 1.0595;   // +1 semitone
const SEMITONE_DOWN_1 = 0.9439; // -1 semitone
const SEMITONE_UP_2 = 1.1225;   // +2 semitones (max +)
const SEMITONE_DOWN_2 = 0.8909; // -2 semitones (max -)

export const FACTION_BGM_PRESETS: Record<FactionId, FactionBgmPreset> = {
  celestial: { playbackRate: SEMITONE_UP_1,   filterType: 'lowpass',  filterCutoffHz: 4000, filterQ: 0.7 },
  runic:     { playbackRate: SEMITONE_DOWN_1, filterType: 'lowpass',  filterCutoffHz: 2500, filterQ: 0.9 },
  techno:    { playbackRate: SEMITONE_UP_2,   filterType: 'highpass', filterCutoffHz: 600,  filterQ: 0.8 },
  elemental: { playbackRate: 1.0,             filterType: 'lowpass',  filterCutoffHz: 3200, filterQ: 0.7 },
  dominion:  { playbackRate: SEMITONE_DOWN_2, filterType: 'lowpass',  filterCutoffHz: 2000, filterQ: 1.0 },
};

/**
 * Resolve preset for an arbitrary value. Unknown faction id, null, or
 * undefined all collapse to NEUTRAL_PRESET. The returned preset's playbackRate
 * is hard-clamped to [PLAYBACK_RATE_MIN, PLAYBACK_RATE_MAX] as a defence
 * against future preset tweaks slipping past review.
 */
export function getFactionBgmPreset(
  factionId: FactionId | string | null | undefined,
): FactionBgmPreset {
  if (factionId == null) return NEUTRAL_PRESET;
  const preset = (FACTION_BGM_PRESETS as Record<string, FactionBgmPreset | undefined>)[factionId];
  if (!preset) return NEUTRAL_PRESET;
  const clamped = Math.max(PLAYBACK_RATE_MIN, Math.min(PLAYBACK_RATE_MAX, preset.playbackRate));
  if (clamped === preset.playbackRate) return preset;
  return { ...preset, playbackRate: clamped };
}